import { all, put, takeEvery, takeLatest } from 'redux-saga/effects';

import { getIdentifierTypes } from '@/common/utils';
import {
  CREATE,
  CREATE_SKU,
  CREATE_SKU_IDENTIFIER,
  DELETE,
  DELETE_SKU,
  DELETE_SUCCESS,
  GET_ALL,
  GET_ALL_SUCCESS,
  GET_BUSINESSES,
  GET_BUSINESSES_SUCCESS,
  GET_DETAIL,
  GET_DETAIL_SUCCESS,
  GET_INTEGRATION,
  GET_INTEGRATION_SUCCESS,
  GET_PRODUCT_PACK_TYPE,
  GET_PRODUCT_PACK_TYPE_SUCCESS,
  GET_PRODUCT_SKU_FILE_SUB_TYPES,
  GET_PRODUCT_SKU_FILE_SUB_TYPES_SUCCESS,
  GET_PRODUCT_TYPES,
  GET_PRODUCT_TYPES_SUCCESS,
  REMOVE_SKU_IDENTIFIER,
  REQUEST_ERROR,
  SORTABLE,
  SORTABLE_SKU,
  UPDATE,
  UPDATE_SKU,
  UPDATE_SKU_FILES,
  UPDATE_SKU_IDENTIFIER,
  UPDATE_SUCCESS,
} from '../types/product';

import {
  getProductList,
  requestBusinesses,
  requestCreateProduct,
  requestDeleteProduct,
  requestGetProductPackType,
  requestGetProductSkuFileSubTypes,
  requestGetProductTypes,
  requestIdentifiers,
  requestIntegrationList,
  requestProductDetail,
  requestSkuFiles,
  requestUpdateProduct,
} from '../requests/product';

import { onFailRequest, onSuccessRequest } from '@/utils';

import {
  createSku,
  createVariantIdentifier,
  deleteSku,
  removeVariantIdentifier,
  updateSku,
  updateSkuFiles,
  updateVariantIdentifier,
} from './product-variant';

/**
 * Get product list
 * @param {*} payload
 */
function* getProduct(payload) {
  try {
    const data = yield getProductList(payload);
    yield put({ type: GET_ALL_SUCCESS, data });
  } catch (error) {
    yield onFailRequest({
      payload,
      error,
      *onCallback() {
        yield put({ type: REQUEST_ERROR });
      },
    });
  }
}

/**
 * Get product detail
 * @param {*} payload
 */
function* getProductDetail(payload) {
  try {
    const { id } = payload;
    const data = yield requestProductDetail(id);
    if (data) {
      const reqFiles = [];
      const reqIdentifiers = [];
      data.product_skus.forEach(({ id: skuId, files, identifiers }) => {
        if (files.length) reqFiles.push(requestSkuFiles(id, skuId));
        if (identifiers.length) reqIdentifiers.push(requestIdentifiers(id, skuId));
      });
      const [files, identifiers] = yield all([all(reqFiles), all(reqIdentifiers)]);
      const identifierTypes = getIdentifierTypes();
      data.product_skus = data.product_skus.map((item, idz) => ({
        ...item,
        files: item.files.length && files[idz].data ? files[idz].data : item.files,
        identifiers:
          item.identifiers.length && identifiers[idz] && identifiers[idz].length
            ? identifiers[idz].map((el) => ({
                ...identifierTypes.find(
                  ({ id: identifierId }) => identifierId === el.product_sku_identifier_type_id,
                ),
                ...el,
              }))
            : [],
      }));
    }
    yield put({
      type: GET_DETAIL_SUCCESS,
      data,
    });
  } catch (error) {
    yield onFailRequest({
      payload,
      error,
      *onCallback() {
        yield put({ type: REQUEST_ERROR });
      },
    });
  }
}

/**
 * Update product
 * @param {*} payload
 */
function* updateProduct(payload) {
  try {
    const response = yield requestUpdateProduct(payload);
    yield put({ type: UPDATE_SUCCESS, data: response });
    if (response) {
      const {
        data: { id },
      } = response;
      yield put({ type: GET_DETAIL, id, isLoading: false });
      yield onSuccessRequest({ response });
    }
  } catch (error) {
    yield onFailRequest({
      payload,
      error,
      *onCallback() {
        yield put({ type: REQUEST_ERROR });
      },
    });
  }
}

/**
 * Sort product list
 * @param {*} payload
 */
function* sortProduct(payload) {
  try {
    yield requestUpdateProduct(payload);
  } catch (error) {
    yield onFailRequest({
      payload,
      error,
      *onCallback() {
        yield put({ type: REQUEST_ERROR });
      },
    });
  }
}

/**
 * Create product
 * @param {*} param0
 */
function* createProduct({ payload, callback }) {
  try {
    const { productData } = payload;
    const response = yield requestCreateProduct(productData);
    const { id: productId } = response.data;
    if (!productId) {
      throw new Error('Create product Fail');
    }

    yield onSuccessRequest({
      response,
      *onCallback() {
        yield callback(response?.data);
      },
    });
  } catch (error) {
    yield onFailRequest({
      payload,
      error,
      *onCallback() {
        yield callback(false);
        yield put({ type: REQUEST_ERROR });
      },
    });
  }
}

/**
 * Delete Sku
 * @param payload
 */
function* deleteProduct(payload) {
  try {
    const { id, callback } = payload;
    const response = yield requestDeleteProduct(id);
    yield put({
      type: DELETE_SUCCESS,
    });
    yield onSuccessRequest({
      response,
      *onCallback() {
        if (callback) yield callback();
      },
    });
  } catch (error) {
    yield onFailRequest({
      payload,
      error,
      *onCallback() {
        yield put({ type: REQUEST_ERROR });
      },
    });
  }
}

/**
 * Get Integration
 * @param payload
 */
function* getIntegrationList(payload) {
  try {
    const { params } = payload;
    const { data } = yield requestIntegrationList(params);
    yield put({
      type: GET_INTEGRATION_SUCCESS,
      data,
    });
  } catch (error) {
    yield onFailRequest({
      payload,
      error,
    });
  }
}

/**
 * Get businesses
 * @param payload
 */
function* getBusinesses(payload) {
  try {
    const { params } = payload;
    const { data } = yield requestBusinesses(params);
    yield put({
      type: GET_BUSINESSES_SUCCESS,
      data: data.length ? [data[0].id] : [],
    });
  } catch (error) {
    yield onFailRequest({
      payload,
      error,
    });
  }
}

function* getProductTypes() {
  try {
    const { data } = yield requestGetProductTypes();
    yield put({ type: GET_PRODUCT_TYPES_SUCCESS, payload: data });
  } catch (error) {
    yield onFailRequest({ error });
  }
}

function* getProductSkuFileSubTypes() {
  try {
    const { data } = yield requestGetProductSkuFileSubTypes();
    yield put({ type: GET_PRODUCT_SKU_FILE_SUB_TYPES_SUCCESS, payload: data });
  } catch (error) {
    yield onFailRequest({ error });
  }
}

function* getProductPackType() {
  try {
    const { data } = yield requestGetProductPackType();
    yield put({ type: GET_PRODUCT_PACK_TYPE_SUCCESS, payload: data });
  } catch (error) {
    yield onFailRequest({ error });
  }
}

export default [
  takeLatest(GET_ALL, getProduct),
  takeLatest(GET_DETAIL, getProductDetail),
  takeLatest(UPDATE, updateProduct),
  takeLatest(DELETE_SKU, deleteSku),
  takeLatest(UPDATE_SKU, updateSku),
  takeLatest(CREATE_SKU, createSku),
  takeLatest(UPDATE_SKU_FILES, updateSkuFiles),
  takeLatest(CREATE_SKU_IDENTIFIER, createVariantIdentifier),
  takeLatest(UPDATE_SKU_IDENTIFIER, updateVariantIdentifier),
  takeLatest(REMOVE_SKU_IDENTIFIER, removeVariantIdentifier),
  takeLatest(CREATE, createProduct),
  takeLatest(SORTABLE, sortProduct),
  takeEvery(SORTABLE_SKU, updateSku),
  takeLatest(DELETE, deleteProduct),
  takeLatest(GET_INTEGRATION, getIntegrationList),
  takeLatest(GET_BUSINESSES, getBusinesses),
  takeLatest(GET_PRODUCT_TYPES, getProductTypes),
  takeLatest(GET_PRODUCT_SKU_FILE_SUB_TYPES, getProductSkuFileSubTypes),
  takeLatest(GET_PRODUCT_PACK_TYPE, getProductPackType),
];
